import { Moment } from "moment";
import moment from "moment-timezone";
import { capitalize } from "serviceshift-ui/shared/src/mixins";
import { customDateFormatWithTimezone } from "@/lib/helpers.js";

import CONSTANTS from "../Constants.js";

export {
  fromNow,
  yearsAndMonthsFromNow,
  extractName,
  capitalize,
  dollars
} from "serviceshift-ui/shared/src/mixins";
export { technicianShowPath } from "@/mixins/path_helpers.js";
export {
  customDateFormatWithTimezone,
  formatDateWithTimezone,
  formatTimeWithTimezone,
  formatDateTimeWithTimezone
} from "@/lib/helpers.js";

export function snakeToPascalCase(stringInput) {
  return stringInput.split("_").map(capitalize).join(" ");
}

export const shortDate = (value) => {
  if (!value) return "";
  return moment(value).format(CONSTANTS.DATE_FORMAT);
};

export const longDate = (value) => {
  if (!value) return "";
  return moment(value).format(CONSTANTS.DATE_FORMAT_LONG);
};

export const datetime = (value) => {
  if (!value) return "";
  return moment(value).format(CONSTANTS.DATE_TIME_FORMAT_YEAR_LONG);
};

export const datetimeLong = (value) => {
  if (!value) return "";
  return moment(value).format(CONSTANTS.DATE_TIME_FORMAT_YEAR_LONG);
};

export const formatTime = (date, includeSeconds) =>
  moment(date).format(
    includeSeconds ? CONSTANTS.TIME_SECONDS_FORMAT : CONSTANTS.TIME_FORMAT
  );

export const formatTableDate = (date) => {
  return moment(date).format(CONSTANTS.DATE_API_FORMAT);
};

export const locationTypeFlags = (locationType) => {
  const locationTypeMap = {
    Residential: {
      commercial_property: false,
      tax_exempt: true
    },
    Commercial: {
      commercial_property: true,
      tax_exempt: false
    },
    TaxExempt: {
      commercial_property: true,
      tax_exempt: true
    },
    Other: {
      commercial_property: false,
      tax_exempt: false
    }
  };
  const trimmedType = locationType.replace(/\s+/g, "");
  return locationTypeMap[trimmedType] || {};
};

export const fullName = (record) => {
  if (!record) {
    return "";
  }

  if (record.full_name) {
    return record.full_name;
  }

  const names = [record.first_name, record.last_name].filter(
    (value) => value !== null
  );
  return `${names.join(" ")}`;
};

export function eventEmitter(event, payload) {
  let vm = this.$parent;
  while (vm) {
    vm.$emit(event, payload);
    vm = vm.$parent;
  }
}

// TODO: Once sales_tax FF is on for everyone, we can simplify this logic
export const locationTypeLabel = (address) => {
  if (address.commercial_property && !address.tax_exempt) {
    return "Commercial";
  }
  if (!address.commercial_property && address.tax_exempt) {
    return "Residential";
  }

  return "Other";
};

export const shortAddress = (address) => {
  if (!address) return "--";

  const { street, unit } = address;
  const streetWithUnit = unit ? `${street}, ${unit}` : street;
  return `${streetWithUnit}`;
};

export const fullAddress = (address) => {
  if (!address) return "--";

  return `${fullAddressLine1(address)}, ${fullAddressLine2(address)}`;
};

export const fullAddressLine1 = (address) => {
  if (!address) return "--";

  const { street, unit } = address;
  return unit ? `${street}, ${unit}` : street;
};

export const fullAddressLine2 = (address) => {
  if (!address) return "--";

  const { city, state, zip_code, zip } = address;
  return `${city}, ${state} ${zip_code || zip}`;
};

export const formatPercent = (percent) => {
  return `${parseFloat((percent * 100).toFixed(2))}%`;
};

export const isStandardComment = (comment) => {
  return (
    comment.user &&
    (!comment.message_key || comment.message_key.includes("Note"))
  );
};

export const displayQuoteStatus = (status) => {
  return CONSTANTS.QUOTE_STATUS_MAP[status]
    ? CONSTANTS.QUOTE_STATUS_MAP[status]
    : status;
};

export const displayAppointmentStatus = (status) => {
  return CONSTANTS.APPOINTMENT_STATUS_MAP[status]
    ? CONSTANTS.APPOINTMENT_STATUS_MAP[status]
    : status;
};

export const humanizeDatasetName = (name) => {
  const words = name.split("_");
  return words.map((word) => capitalize(word)).join(" ");
};

export const formatDuration = (durationMinutes = 0, includeMins = false) => {
  const sign = durationMinutes < 0 ? "-" : "";
  const duration = durationMinutes
    ? Number(Math.abs(durationMinutes).toFixed(2))
    : 0;
  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;
  let str = "";
  if (hours > 0) {
    str += `${(hours || 0).toLocaleString()}h `;
  }
  str = str + `${hours && minutes < 10 ? `0${minutes}` : minutes}m`;
  if (includeMins && hours > 0) {
    str += ` (${(duration || 0).toLocaleString()} mins)`;
  }
  return sign + str;
};

export const formatDurationSeconds = (durationSeconds = 0) => {
  const sign = durationSeconds < 0 ? "-" : "";
  const duration = durationSeconds
    ? Number(Math.abs(durationSeconds).toFixed(2))
    : 0;
  const seconds = duration % 60;
  const minutes = Math.floor(duration / 60) % 60;
  const hours = Math.floor(duration / 3600);

  let str = "";
  if (hours > 0) {
    str += `${(hours || 0).toLocaleString()}h `;
  }
  if (minutes > 0) {
    str = str + `${hours && minutes < 10 ? `0${minutes}` : minutes}m `;
  }

  str = str + `${minutes && seconds < 10 ? `0${seconds}` : seconds}s`;

  return sign + str;
};

export const scheduledDateTime = (
  scheduledTime,
  arrivalWindowStart,
  arrivalWindowEnd,
  includeDate = true
) => {
  const cawFFEnabled =
    window.currentTenant.feature_flags.custom_arrival_windows_enabled;

  if (!cawFFEnabled) {
    const date = moment(scheduledTime).format(CONSTANTS.DATE_FORMAT);
    const start = moment(scheduledTime)
      .subtract(1, "hour")
      .format(CONSTANTS.TIME_FORMAT);
    return `${date} - ${start}`;
  }
  const date = moment(scheduledTime).format(CONSTANTS.DATE_FORMAT);
  const start = arrivalWindowStart || moment(scheduledTime).subtract(1, "hour");
  const startTime = moment(start).format(CONSTANTS.TIME_FORMAT);
  const endTime = moment(arrivalWindowEnd || scheduledTime).format(
    CONSTANTS.TIME_FORMAT
  );
  return `${includeDate ? `${date} - ` : ""}${startTime} to ${endTime}`;
};

export const getInitials = (technician) => {
  if (!technician) {
    return "";
  }
  if (technician.full_name) {
    return technician.full_name
      .trim()
      .split(" ")
      .map((name) => name[0])
      .join("");
  }
  return `${(technician.first_name || "")[0]}${
    (technician.last_name || "")[0]
  } `;
};

export const durationLabel = (currentDuration, serviceCategory) => {
  if (!serviceCategory) {
    return formatDuration(currentDuration);
  }
  const serviceCategoryDefault =
    currentDuration === serviceCategory.duration_minutes;
  const label = serviceCategoryDefault ? ` from ${serviceCategory.name}` : "";
  return `${formatDuration(currentDuration)}${label}`;
};

export const remainingDurationLabel = (remainingDuration) => {
  const pastEstimate = remainingDuration < 0;
  let label = "";
  if (pastEstimate) {
    label = " Past Estimate";
  }
  return `${pastEstimate ? "-" : ""}${formatDuration(
    Math.abs(remainingDuration)
  )}${label}`;
};

export const calculateRemainingDuration = (appointment) => {
  const {
    dispatch_time,
    min_arrival_time,
    duration_minutes,
    estimated_end_time
  } = appointment;

  const startTime = moment(dispatch_time || min_arrival_time);
  // If start time is in the future, remaining is equal to the full duration
  if (startTime.isAfter(moment())) {
    return duration_minutes;
  }
  // If start time is in the past, add remaining minutes to now
  // else add remaining minutes to future start time
  return moment(estimated_end_time).diff(moment(), "minutes");
};

export const getCombinedDateTime = (date, time): Moment => {
  const day = moment(date).format(CONSTANTS.DATE_API_FORMAT);
  let newTime = time;
  // Sometimes the time is passed in as a date. If so, just format as a time string instead
  if (Date.parse(time) > 0) {
    newTime = customDateFormatWithTimezone(time, CONSTANTS.TIME_PICKER_FORMAT);
  }
  return moment(
    day + " " + newTime,
    `${CONSTANTS.DATE_API_FORMAT} ${CONSTANTS.TIME_FORMAT_LONG}`
  );
};

export const assignedTechnicianLabel = (technician, pinned, pinnedReason) => {
  if (!technician) {
    return "--";
  }
  const reason = pinnedReason || "N/A";
  return `${technician.full_name}${pinned ? `; Pinned: ${reason}` : ""}`;
};

export const resolveAssignedTechnicians = (appointment) => {
  const { technicians, technician } = appointment;
  if (technicians?.length > 0) return technicians;
  return technician ? [technician] : [];
};

export const assignedTechniciansLabel = (technicians, pinned, pinnedReason) => {
  if (!technicians || technicians.length === 0) return "--";
  if (technicians.length === 1) {
    return assignedTechnicianLabel(technicians[0], pinned, pinnedReason);
  }
  const names = technicians.map((t) => t.full_name).filter(Boolean);
  if (names.length === 0) return "--";
  const label = names.join(", ");
  const reason = pinnedReason || "N/A";
  return `${label}${pinned ? `; Pinned: ${reason}` : ""}`;
};

export const fileSize = (file) => {
  if (!file) {
    return null;
  }
  // Calculate file size
  const kb = file.size * 0.001;
  const mb = kb * 0.001;
  let size = kb;
  let unit = "KB";
  if (mb > 1) {
    size = mb;
    unit = "MB";
  }
  return `${size.toFixed(1)} ${unit}`;
};

export const breakpoints = () =>
  Object.keys(CONSTANTS.BREAKPOINTS).reduce(
    (breakpoints, key) => {
      breakpoints[key] = window.innerWidth < CONSTANTS.BREAKPOINTS[key];
      return breakpoints;
    },
    {
      sm: false,
      md: false,
      lg: false,
      xl: false
    }
  );

export const addOrdinalSuffix = (i: number): string => {
  const j = i % 10,
    k = i % 100;
  if (j === 1 && k !== 11) {
    return i + "st";
  }
  if (j === 2 && k !== 12) {
    return i + "nd";
  }
  if (j === 3 && k !== 13) {
    return i + "rd";
  }
  return i + "th";
};

export const formatTimeDuration = (duration) => {
  return `${duration.hours() > 0 ? duration.hours() + "h " : ""}${
    duration.minutes() < 10 ? "0" : ""
  }${duration.minutes()}m`;
};
